import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TIPOS_AGENDA = [
  { id: 'todos', label: 'Todos', icon: 'List' },
  { id: 'llamada', label: 'Llamada', icon: 'Phone' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' },
  { id: 'email', label: 'Email', icon: 'Mail' },
  { id: 'cita', label: 'Cita', icon: 'Calendar' },
  { id: 'seguimiento', label: 'Seguimiento', icon: 'RefreshCw' },
  { id: 'mensaje', label: 'Mensaje', icon: 'MessageSquare' },
];

const RANGOS_FECHA = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes', label: 'Este mes' },
  { id: 'todos', label: 'Todos' },
];

const ESTADO_COLORS = {
  pendiente: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  realizado: 'bg-green-100 text-green-700 border-green-200',
  cancelado: 'bg-red-100 text-red-700 border-red-200',
};

const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  realizado: 'Realizado',
  cancelado: 'Cancelado',
};

const getTipoInfo = (tipo) => {
  return TIPOS_AGENDA?.find(t => t?.id === tipo) || { id: tipo, label: tipo, icon: 'Activity' };
};

const formatFecha = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha + 'T00:00:00');
  return d?.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const formatFechaCorta = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha + 'T00:00:00');
  return d?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isToday = (fecha) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  return fecha === today;
};

const isThisWeek = (fecha) => {
  const today = new Date();
  const d = new Date(fecha + 'T00:00:00');
  const startOfWeek = new Date(today);
  startOfWeek?.setDate(today?.getDate() - today?.getDay());
  startOfWeek?.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek?.setDate(startOfWeek?.getDate() + 6);
  endOfWeek?.setHours(23, 59, 59, 999);
  return d >= startOfWeek && d <= endOfWeek;
};

const isThisMonth = (fecha) => {
  const today = new Date();
  const d = new Date(fecha + 'T00:00:00');
  return d?.getMonth() === today?.getMonth() && d?.getFullYear() === today?.getFullYear();
};

const AgendaView = ({ activities, loading, onNuevaActividad, onUpdateStatus, onEnviarWhatsApp, onAgendar }) => {
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [rangoFiltro, setRangoFiltro] = useState('semana');

  const filtered = useMemo(() => {
    return activities?.filter(act => {
      const tipoOk = tipoFiltro === 'todos' || act?.tipo === tipoFiltro;
      let fechaOk = true;
      if (rangoFiltro === 'hoy') fechaOk = isToday(act?.fecha);
      else if (rangoFiltro === 'semana') fechaOk = isThisWeek(act?.fecha);
      else if (rangoFiltro === 'mes') fechaOk = isThisMonth(act?.fecha);
      return tipoOk && fechaOk;
    });
  }, [activities, tipoFiltro, rangoFiltro]);

  // Group by fecha
  const grouped = useMemo(() => {
    const groups = {};
    filtered?.forEach(act => {
      const key = act?.fecha || 'sin-fecha';
      if (!groups?.[key]) groups[key] = [];
      groups?.[key]?.push(act);
    });
    return Object.entries(groups)?.sort(([a], [b]) => a?.localeCompare(b));
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-wrap">
          {TIPOS_AGENDA?.map(t => (
            <button
              key={t?.id}
              onClick={() => setTipoFiltro(t?.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${tipoFiltro === t?.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name={t?.icon} size={12} />
              {t?.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {RANGOS_FECHA?.map(r => (
            <button
              key={r?.id}
              onClick={() => setRangoFiltro(r?.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${rangoFiltro === r?.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {r?.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto">
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={onNuevaActividad}>
            Nueva actividad
          </Button>
        </div>
      </div>

      {grouped?.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <Icon name="CalendarDays" size={40} className="text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground text-sm">No hay actividades para el período seleccionado</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped?.map(([fecha, acts]) => (
            <div key={fecha}>
              {/* Date header */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isToday(fecha) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {isToday(fecha) ? 'Hoy' : formatFechaCorta(fecha)}
                </div>
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">{acts?.length} actividad{acts?.length !== 1 ? 'es' : ''}</span>
              </div>

              {/* Activities */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                {acts?.map((act, idx) => {
                  const tipo = getTipoInfo(act?.tipo);
                  return (
                    <div key={act?.id} className={`flex items-start gap-4 px-4 py-3 ${idx < acts?.length - 1 ? 'border-b border-border' : ''} hover:bg-muted/20 transition-colors`}>
                      {/* Time */}
                      <div className="w-14 flex-shrink-0 text-center">
                        <span className="text-sm font-mono font-medium text-foreground">
                          {act?.hora ? act?.hora?.slice(0, 5) : '—:—'}
                        </span>
                      </div>

                      {/* Type icon */}
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={tipo?.icon} size={14} className="text-primary" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <span className="text-sm font-medium text-foreground">{tipo?.label}</span>
                            {act?.contact?.nombre && (
                              <span className="text-sm text-muted-foreground ml-2">— {act?.contact?.nombre}</span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ESTADO_COLORS?.[act?.estado] || ESTADO_COLORS?.pendiente}`}>
                            {ESTADO_LABELS?.[act?.estado] || act?.estado}
                          </span>
                        </div>
                        {act?.notas && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">{act?.notas}</p>
                        )}
                        {act?.responsable_user?.full_name && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <Icon name="User" size={10} className="inline mr-1" />
                            {act?.responsable_user?.full_name}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      {act?.estado === 'pendiente' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {(act?.tipo === 'whatsapp' || act?.contact?.whatsapp || act?.contact?.telefono) && (
                            <button
                              onClick={() => onEnviarWhatsApp(act?.contact, act?.notas)}
                              title="Enviar WhatsApp"
                              className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                            >
                              <Icon name="MessageCircle" size={14} />
                            </button>
                          )}
                          {onAgendar && act?.contact && (
                            <button
                              onClick={() => onAgendar(act?.contact, act)}
                              title="Agendar trabajo"
                              className="flex items-center gap-1 p-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors text-xs font-medium px-2"
                            >
                              <Icon name="CalendarPlus" size={13} />
                              Agendar
                            </button>
                          )}
                          <button
                            onClick={() => onUpdateStatus(act?.id, 'realizado')}
                            title="Marcar realizada"
                            className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors text-xs font-medium px-2"
                          >
                            ✓ Realizada
                          </button>
                          <button
                            onClick={() => onUpdateStatus(act?.id, 'cancelado')}
                            title="Cancelar"
                            className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgendaView;
